<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">{{ location }}</h2>
      <span>|</span>
      <h2 class="title title-primary">
        {{ $t("lang.movement-entry.order_boxes.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.movement-entry.order_boxes.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes" v-if="!shouldLoading">
        <bx-box
          dark
          color="outline"
          :title="$t('lang.movement-entry.order_boxes.box_title_1')"
          class="col-12 col-md-6 col-lg-6 col-xl-auto p-1"
        >
          <template slot="actions">
            <bx-order-status
              :status="order_status"
              dark
            ></bx-order-status>
          </template>
          <div class="detail-titles">
            <div class="wrapper">
              <h4>
                Nº
                <strong>{{ id }}</strong>
              </h4>
              <h5>
                <strong>{{ name }}</strong>
              </h5>
            </div>
          </div>
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-lg-6 col-xl-auto p-1">
          <bx-value
            :label="$t('lang.movement-entry.order_boxes.box_label_2')"
            :value="arrivalTime"
            font-size="xs"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-6 col-md-4 col-lg-4 col-xl-auto p-1 bx-box-adjustable"
        >
          <bx-value
            :label="$t('lang.movement-entry.order_boxes.box_label_3')"
            :value="waitingTime"
            font-size="xs"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-6 col-md-4 col-lg-4 col-xl-auto p-1"
        >
          <bx-value
            :label="$t('lang.movement-entry.order_boxes.box_label_4')"
            :value="
              `${orderData.destination_real_item_count} / ${orderData.item_count}`
            "
            font-size="xs"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-6 col-md-4 col-lg-4 col-xl-auto p-1"
        >
          <bx-value
            :label="$t('lang.movement-entry.order_boxes.box_label_5')"
            :value="
              `${orderData.destination_num_verified_boxes} / ${orderData.num_boxes}`
            "
            font-size="xs"
          />
        </bx-box>
      </div>
      <div class="boxes" v-else>
        <div class="col-12 col-md-4 col-lg-3 p-1">
          <PuSkeleton height="10px"></PuSkeleton>
          <PuSkeleton height="90px" class="m-t-5"></PuSkeleton>
        </div>
        <div class="col-6 col-md-4 col-lg-2 p-1">
          <div style="height: 20px"></div>
          <PuSkeleton height="90px" class="m-t-5"></PuSkeleton>
        </div>
        <div class="col-6 col-md-4 col-lg-1 p-1">
          <div style="height: 20px"></div>
          <PuSkeleton height="90px" class="m-t-5"></PuSkeleton>
        </div>
        <div class="col-6 col-lg-1 p-1">
          <div style="height: 20px"></div>
          <PuSkeleton height="90px" class="m-t-5"></PuSkeleton>
        </div>
        <div class="col-6 col-lg-1 p-1">
          <div style="height: 20px"></div>
          <PuSkeleton height="90px" class="m-t-5"></PuSkeleton>
        </div>
      </div>
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row" style="margin: 8px">
          <bx-btn @click="$router.back()">{{
            $t("lang.messages.go_back")
          }}</bx-btn>
        </div>
        <v-row class="d-flex d-sm-none">
          <v-list width="100%">
            <v-list-item
              two-line
              v-for="(item, index) in data"
              :key="index"
              class="green lighten-5 ma-2 rounded-lg"
              ripple
              :to="`/movements/entry/order/${id}/box/${item.id}/detail`"
            >
              <v-list-item-content>
                <v-list-item-title>
                  <div>
                    {{ item.barcode }}
                  </div>
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-list-item-subtitle class="text-caption">
                  {{ item.real_item_count + "/" + item.theorical_item_count }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">
                  {{ item.reception_time }}
                </v-list-item-subtitle>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-row>
        <div class="row">
          <div class="col-12">
            <div
              class="row p-1 mx-1"
              style="background: white"
              v-if="shouldLoading"
            >
              <PuSkeleton height="100px" class="col-12"></PuSkeleton>
              <PuSkeleton
                height="250px"
                class="col-12"
                style="margin-top: 10px"
              ></PuSkeleton>
            </div>
            <bx-box
              :title="
                itemCount +
                  ' ' +
                  $t('lang.movement-entry.order_boxes.table_title')
              "
              color="white"
              no-padding
              v-if="!shouldLoading"
            >
              <template v-slot:default>
                <bx-table
                  :data="data"
                  :columns="columns"
                  paginated
                  backendPagination
                  :currentPage="page"
                  :perPage="itemsPerPage"
                  :total="itemCount"
                  @update:currentPage="updatePage"
                  @search="onSearch"
                >
                  <template slot-scope="props">
                    <bx-table-column field="box_status" centered>
                      <bx-box-status
                        :status="props.row.box_status"
                      ></bx-box-status>
                    </bx-table-column>
                    <bx-table-column>
                      <router-link
                        :to="
                          `/movements/entry/order/${id}/box/${props.row.id}/detail`
                        "
                        class="bx-link bx-link-bold"
                        >{{ props.row.barcode }}</router-link
                      >
                    </bx-table-column>
                    <bx-table-column>{{
                      formatDate(props.row.arrival_time)
                    }}</bx-table-column>
                    <bx-table-column>{{
                      props.row.theorical_item_count
                    }}</bx-table-column>
                    <bx-table-column>{{
                      props.row.real_item_count
                    }}</bx-table-column>
                    <bx-table-column>{{
                      props.row.references
                    }}</bx-table-column>
                    <bx-table-column>
                      <router-link
                        class="bx-link bx-link-bold"
                        :to="{
                          name: 'Movement_Entry_BoxVerificationList',
                          params: { orderId: id, id: props.row.id }
                        }"
                      >
                        {{ props.row.verification_count }}
                      </router-link>
                    </bx-table-column>
                    <bx-table-column>
                      <bx-complementary-mini
                        :percent="props.row.ratingPercent"
                      ></bx-complementary-mini>
                    </bx-table-column>
                  </template>
                </bx-table>
              </template>
            </bx-box>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BxTable from "../../../bx-components/BxTable";
import BxTableColumn from "../../../bx-components/BxTableColumn";
import BxBtn from "../../../bx-components/BxBtn";
import BxReceptionBoxStatus from "../../../bx-components/BxReceptionBoxStatus";
import moment from "moment";

export default {
  name: "movement-entry-order-detail",
  components: { BxReceptionBoxStatus, BxBtn, BxTableColumn, BxTable },
  props: {
    id: {
      type: [Number, String]
    }
  },
  data() {
    return {
      shouldLoading: true,
      columns: [],
      data: [],
      orderData: {},
      arrivalTime: "",
      waitingTime: "",
      itemCount: "",
      name: "",
      order_status: 0,
      contents: [],
      reloadInterval: null,
      API_BASE: process.env.VUE_APP_API_BASE,
      itemsPerPage: process.env.VUE_APP_ORDER_BOXES_PER_PAGE,
      page: 1,
      filters: {
        box__codebar__contains: null
      }
    };
  },
  computed: {
    location() {
      return localStorage.getItem("location");
    }
  },
  methods: {
    formatDate(date) {
      return date
        ? moment(date).format("DD/ MM/ YYYY, h:mm a")
        : this.$t("lang.messages.no_date_available");
    },
    onSearch(field, value) {
      this.$session.set(this.key + field, value);

      switch (field) {
        case "barcode":
          this.filters.barcode = value;
          this.getData();
          break;
      }
    },
    updatePage(p) {
      this.page = p;
      this.getData(this.locationId);
    },
    async getData() {
      try {
        this.orderData = await this.$api.morders.get(this.id);
        this.arrivalTime = this.orderData.arrival_date;
        this.order_status = this.orderData.destination_status;
        this.waitingTime = this.orderData.destination_waiting_time;
        this.name = this.orderData.order_name;
        this.destination_num_verified_boxes = this.orderData.destination_num_verified_boxes;

        this.report_boxes = await this.$api.morders.report_boxes(this.id, {
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          box__codebar__contains: this.filters.barcode
        });

        this.itemCount = this.report_boxes.count;
        this.data = this.report_boxes.results.map((item, key) => {
          return {
            id: item.id,
            box_status: item.status,
            barcode: item.barcode,
            arrival_time: item.reading_time,
            theorical_item_count: item.theorical_item_count,
            real_item_count: item.real_item_count,
            references: item.references,
            verification_count: item.verification_count,
            ratingPercent:
              (item.matched_item_count / item.theorical_item_count) * 100
          };
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.columns = [
          {
            field: "box_status",
            label: this.$t("lang.movement-entry.order_boxes.table_field_1"),
            centered: true,
            width: 80
          },
          {
            field: "barcode",
            label: this.$t("lang.movement-entry.order_boxes.table_field_2"),
            filter: {
              placeholder: this.$t(
                "lang.movement-entry.order_boxes.table_field_placeholder_2"
              ),
              value: this.filters.barcode,
              isdate: false
            }
          },
          {
            field: "arrival_time",
            label: this.$t("lang.movement-entry.order_boxes.table_field_3"),
            width: 130
          },
          {
            field: "theorical_item_count",
            label: this.$t("lang.movement-entry.order_boxes.table_field_4")
          },
          {
            field: "real_item_count",
            label: this.$t("lang.movement-entry.order_boxes.table_field_5")
          },
          {
            field: "references",
            label: this.$t("lang.movement-entry.order_boxes.table_field_6")
          },
          {
            field: "verification_count",
            label: this.$t("lang.movement-entry.order_boxes.table_field_7")
          },
          {
            field: "ratingPercent",
            label: this.$t("lang.movement-entry.order_boxes.table_field_8"),
            width: 200
          }
        ];
        this.shouldLoading = false;
      }
    }
  },
  mounted() {
    this.reloadInterval = setInterval(
      this.getData,
      process.env.VUE_APP_RELOAD_TIME
    );
    this.filters.barcode = this.$session.get(this.key + "barcode");
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>

<style scoped>
@media screen and (max-width: 480px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 95px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 10px;
  }
}

@media screen and (max-width: 411px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 79px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 0px;
  }
}

@media screen and (max-width: 375px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 91px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 10px;
  }
}
</style>
